import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type JsonFileValueSourceIndicatorProps = {
  onClear?: (() => void) | undefined;
};

/**
 * Shows that a transaction value came from an imported JSON file.
 */
export function JsonFileValueSourceIndicator({
  onClear,
}: JsonFileValueSourceIndicatorProps): React.ReactElement {
  return (
    <TooltipProvider>
      <div
        className={cn(
          'relative flex items-center gap-[5px] rounded-[70px] border border-zinc-100 bg-white py-[5px] pl-[10px]',
          onClear ? 'pr-[22px]' : 'pr-[10px]'
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="flex items-center gap-[5px] outline-none"
            >
              <Upload className="h-[10px] w-[10px] shrink-0" />
              <span className="text-[10px] font-medium leading-none text-black">
                JSON File
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            This value was imported from a JSON file
          </TooltipContent>
        </Tooltip>

        {onClear && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onClear}
                aria-label="Clear value imported from JSON file"
                className="absolute right-[5px] top-1/2 !h-[11px] !w-[11px] -translate-y-1/2 rounded-[58px] p-[2px] hover:bg-zinc-100"
              >
                <svg
                  className="!h-[11px] !w-[11px]"
                  viewBox="0 0 8 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle
                    cx="4"
                    cy="4"
                    r="3.3"
                    stroke="#52525B"
                    strokeWidth="0.8"
                  />
                  <path
                    d="M2.85 2.85L5.15 5.15"
                    stroke="#52525B"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                  />
                  <path
                    d="M5.15 2.85L2.85 5.15"
                    stroke="#52525B"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                  />
                </svg>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Clear value imported from JSON file</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
